import React, { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, MapPin, MessageCircle } from 'lucide-react';
import './ChatWindow.css';

const ChatWindow = ({ conversation, messages, loading, sending, onSend, onBack }) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const content = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    await onSend(content);
  };

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const groupByDate = (msgs) => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    msgs.forEach((msg) => {
      const date = new Date(msg.created_at).toLocaleDateString('fr-FR');
      if (date !== currentDate) {
        if (currentGroup.length > 0) groups.push({ date: currentDate, messages: currentGroup });
        currentDate = date;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) groups.push({ date: currentDate, messages: currentGroup });
    return groups;
  };

  const formatDateLabel = (dateStr) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateStr === today.toLocaleDateString('fr-FR')) return "Aujourd'hui";
    if (dateStr === yesterday.toLocaleDateString('fr-FR')) return 'Hier';

    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    return dateStr;
  };

  if (!conversation) {
    return (
      <div className="chat-empty-state">
        <div className="chat-empty-icon-wrap">
          <MessageCircle size={32} strokeWidth={1.7} />
        </div>
        <h3>Vos messages</h3>
        <p>Sélectionnez une conversation pour échanger avec un salon</p>
      </div>
    );
  }

  const grouped = groupByDate(messages);

  return (
    <div className="chat-window">
      <div className="chat-header">
        <button type="button" className="chat-back-btn" onClick={onBack} aria-label="Retour">
          <ArrowLeft size={20} />
        </button>
        <div className="chat-salon-avatar">
          {conversation.salon_profile_image ? (
            <img src={conversation.salon_profile_image} alt="" />
          ) : (
            <span>{(conversation.salon_name || '??').slice(0, 2).toUpperCase()}</span>
          )}
        </div>
        <div className="chat-salon-info">
          <h3>{conversation.salon_name}</h3>
          <div className="chat-salon-meta">
            {conversation.salon_city && (
              <span>
                <MapPin size={12} /> {conversation.salon_city}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">
            <div className="chat-spinner" />
            <p>Chargement…</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-no-messages">
            <div className="chat-empty-icon-wrap sm">
              <MessageCircle size={22} strokeWidth={1.7} />
            </div>
            <p>Aucun message pour l’instant</p>
            <small>Dites bonjour au salon</small>
          </div>
        ) : (
          <div className="chat-messages-content">
            {grouped.map((group, idx) => (
              <div key={idx} className="chat-group">
                <div className="chat-date-sep">
                  <span>{formatDateLabel(group.date)}</span>
                </div>
                {group.messages.map((msg) => {
                  const isSent = msg.sender_type === 'CLIENT';
                  const isSystem = msg.message_type === 'system';

                  if (isSystem) {
                    return (
                      <div key={msg.id} className="msg-system">
                        <div className="msg-bubble">
                          <p>{msg.content}</p>
                          <span className="msg-time">{formatTime(msg.created_at)}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className={`msg ${isSent ? 'sent' : 'received'}`}>
                      <div className="msg-bubble">
                        <p>{msg.content}</p>
                        <span className="msg-time">{formatTime(msg.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="chat-input">
        <textarea
          ref={textareaRef}
          placeholder="Votre message…"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            adjustHeight();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={sending}
          rows={1}
        />
        <button type="button" onClick={handleSend} disabled={!input.trim() || sending} aria-label="Envoyer">
          {sending ? <div className="chat-spinner-small" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
